import { useEffect, type RefObject } from 'react';
import { useAiSelectionStore } from '../../../terminal/store/aiSelectionStore';
import { findSelectionRangeByText } from '../utils/editorSelectionUtils';
import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import type { QueryEditorHandle } from '../components/QueryEditor';

type UseEditorSelectionRequestsParams = {
  tabId: string;
  editorRef: RefObject<QueryEditorHandle | null>;
};

/**
 * Subscribes to the AI selection-request bus (`aiSelectionStore`) and applies
 * an externally-requested selection to this tab's editor. Requests arrive from
 * MCP / AI tooling keyed by tabId; the applier retries on a bounded schedule
 * until the editor is mounted and the range resolves, then clears the request.
 * Behavior carried verbatim from the previous inline effect.
 */
export function useEditorSelectionRequests({
  tabId,
  editorRef,
}: UseEditorSelectionRequestsParams) {
  const clearSelectionRequest = useAiSelectionStore((s) => s.clearSelectionRequest);
  const selectionRequestEnvelope = useAiSelectionStore(
    (s) => s.selectionRequestsByTabId.get(tabId),
  );

  useEffect(() => {
    if (!selectionRequestEnvelope) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attemptsLeft = 12;

    const tryApply = () => {
      if (cancelled) return;

      const editor = editorRef.current;
      if (!editor) {
        attemptsLeft -= 1;
        if (attemptsLeft <= 0) {
          clearSelectionRequest(tabId);
          return;
        }
        retryTimer = setTimeout(tryApply, 150);
        return;
      }

      const request = selectionRequestEnvelope.request;
      const applyRange = (range: EditorSelectionRange | null): boolean => {
        if (!range) return false;
        return editor.setSelectionRange(range);
      };

      let applied = false;

      if (request.type === 'range') {
        applied = applyRange(request.range);
      } else if (request.type === 'text') {
        const content = editor.getValue() || '';
        const range = findSelectionRangeByText(content, request.text, request.match ?? 'last');
        applied = applyRange(range);
      }

      if (applied) {
        clearSelectionRequest(tabId);
        return;
      }

      attemptsLeft -= 1;
      if (attemptsLeft <= 0) {
        clearSelectionRequest(tabId);
        return;
      }

      retryTimer = setTimeout(tryApply, 200);
    };

    tryApply();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [clearSelectionRequest, selectionRequestEnvelope, tabId, editorRef]);
}
